import { CurrentStatus, Reservation } from 'types/reservations';
import { ReservationShift } from 'types/shifts';
import { runsIntoBlock, timeToBlock } from './helper';

type ShiftWindow = {
  start: number;
  close: number;
};

const isOpen = (s: ReservationShift) => s.active && !s.closed && !s.disabled;

// The latest open shift that has started. Null before the first one, and on
// days with a single shift, where the header offers no way back to the day.
export const getRunningShiftId = (
  shifts: ReservationShift[],
  block: number,
) => {
  const open = shifts.filter(isOpen);

  if (open.length < 2) return null;

  const started = open.filter((s) => s.start <= block);
  const running = started.filter((s) => s.close >= block);

  // Between two shifts the ended one stays. Of two shifts starting together
  // the one ending first wins, an all-day shift next to it would show the day.
  return (
    (running.length ? running : started).sort(
      (a, b) => b.start - a.start || a.close - b.close,
    )[0]?.id ?? null
  );
};

// Consecutive shifts share their edge block, it belongs to the one that
// starts there. Shifts running in parallel keep their full window, and so does
// a shift whose follower ends before it does.
export const getLastBlockOfShift = (
  shift: ReservationShift,
  shifts: ReservationShift[],
) => {
  const next = shifts
    .filter(
      (s) =>
        isOpen(s) &&
        s.start > shift.start &&
        s.start >= (shift.service ?? shift.close) &&
        s.start <= shift.close &&
        s.close >= shift.close,
    )
    .map((s) => s.start);

  return next.length ? Math.min(...next) - 1 : shift.close;
};

export const getShiftOfReservation = (
  reservation: Pick<Reservation, 'occassion' | 'startTimeInBlocks'>,
  shifts: ReservationShift[],
  cutAtNextShift: boolean,
) => {
  const start = reservation.startTimeInBlocks;
  const takes = (s: ReservationShift) =>
    s.active &&
    (!s.occassions.length || s.occassions.includes(reservation.occassion)) &&
    s.start <= start;

  const own = shifts.find((s) => takes(s) && s.close >= start);

  if (!own || !cutAtNextShift || getLastBlockOfShift(own, shifts) >= start)
    return own?.id ?? '';

  // The cut moved the block to the shift starting there, the one the book
  // opens. It lists the reservation also when it does not take the occasion.
  return (
    shifts
      .filter(
        (s) =>
          isOpen(s) &&
          s.start <= start &&
          getLastBlockOfShift(s, shifts) >= start,
      )
      .sort((a, b) => b.start - a.start || a.close - b.close)[0]?.id ?? ''
  );
};

export const isReservationVisibleInShift = (
  reservation: Reservation,
  shift: ShiftWindow | null | undefined,
  hideLiveReservations: boolean,
  currentTime: string | null,
  strictShiftStartFilter = false,
) => {
  if (!shift) return true;

  const start = reservation.startTimeInBlocks ?? timeToBlock(reservation.time);

  if (strictShiftStartFilter) {
    return start >= shift.start && start <= shift.close;
  }

  if (
    (reservation.currentStatus === CurrentStatus.SEATED ||
      reservation.currentStatus === CurrentStatus.HAS_ORDERED ||
      reservation.currentStatus === CurrentStatus.PAID) &&
      !hideLiveReservations
  ) {
    return true;
  }

  if (start >= shift.start) return start <= shift.close;

  return runsIntoBlock(reservation, shift.start, currentTime);
};
